import type { FilterDropdownOption } from '~/components/filter/FilterDropdown.vue'
import type { Sort } from '~/contracts/SearchRepositoryContract'

export interface SortOption {
  label: string
  value: Sort | undefined
}

export function useProductSortingOptions() {
  const { $t } = useNuxtApp()

  /* Sort query */
  const route = useRoute()
  const router = useRouter()
  const sortQuery = computed<string | undefined>(() => String(route.query.sort) || undefined)

  /* Sort options */
  const sortOptions = ref<SortOption[]>([
    { label: $t('sortDefault'), value: undefined },
    { label: $t('sortPriceAscending'), value: 'lowestPrice:asc' },
    { label: $t('sortPriceDescending'), value: 'lowestPrice:desc' },
    { label: $t('sortSaleDescending'), value: 'highestDiscountPercentage:desc' },
  ])
  const selectedSortOption = computed<SortOption | undefined>(() => {
    return sortOptions.value.find(so => so.value === sortQuery.value)
      || sortOptions.value.find(so => so.value === undefined)
  })
  const sortedBy = computed<Sort | undefined>(() => {
    return selectedSortOption.value?.value
  })

  /* Sorting */
  function sortBy(sort: string | undefined) {
    const option = sortOptions.value.find(so => so.value === sort)
    if (!option)
      return

    window.scrollTo({ top: 0, behavior: 'instant' })
    router.replace({ query: { ...route.query, sort } })
  }

  /* Dropdown options - Dropdowns are not allowed to have undefined values */
  const sortOptionsForDropdown = computed<FilterDropdownOption[]>(() =>
    sortOptions.value.map(so => ({ label: so.label, value: so.value ?? 'default' })),
  )
  const selectedSortOptionForDropdown = computed<FilterDropdownOption | undefined>(() => {
    const so = selectedSortOption.value
    return so ? { label: so.label, value: so.value ?? 'default' } : undefined
  })
  function selectSortOption({ value }: FilterDropdownOption) {
    sortBy(value === 'default' ? undefined : value)
  }

  return {
    sortedBy,

    // Dropdown options
    sortOptionsForDropdown,
    selectedSortOptionForDropdown,
    selectSortOption,
  }
}
